import axios from 'axios';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://cashflow.silverwal.com/api',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', 
      },
      withCredentials: false, // Set to false for token-based auth
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async register(userData) {
    try {
      const response = await this.api.post('/register', userData);
      if (response.data.success && response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error.response?.data);
      throw error;
    }
  }

  async login(credentials) {
    try {
      const response = await this.api.post('/login', credentials);
      if (response.data.success && response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error.response?.data);
      throw error;
    }
  }

  async logout() {
    try {
      await this.api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }

  async getUser() {
    const response = await this.api.get('/user');
    if (response.data.success) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  async updateProfile(data) {
    const response = await this.api.put('/profile', data);
    if (response.data.success) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  // Utility methods
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  isAuthenticated() {
    return !!localStorage.getItem('auth_token');
  }

  // Currency methods
  async getSupportedCurrencies() {
    const response = await this.api.get('/currencies');
    return response.data.currencies;
  }

  // Dashboard
  async getDashboard(period = 'month') {
    const response = await this.api.get(`/dashboard?period=${period}`);
    return response.data;
  }

  // Categories
  async getCategories(type = null) {
    const url = type ? `/categories?type=${type}` : '/categories';
    const response = await this.api.get(url);
    return response.data.data || [];
  }

  async createCategory(data) {
    const response = await this.api.post('/categories', data);
    return response.data.data;
  }

  async updateCategory(id, data) {
    const response = await this.api.put(`/categories/${id}`, data);
    return response.data.data;
  }

  async deleteCategory(id) {
    await this.api.delete(`/categories/${id}`);
  }

  // Budgets
  async getBudgets() {
    const response = await this.api.get('/budgets');
    return response.data.data || [];
  }

  async createBudget(data) {
    const response = await this.api.post('/budgets', data);
    return response.data.data;
  }

  async updateBudget(id, data) {
    const response = await this.api.put(`/budgets/${id}`, data);
    return response.data.data;
  }

  async deleteBudget(id) {
    await this.api.delete(`/budgets/${id}`);
  }

  // Transactions
  async getTransactions(filters = {}) {
    const params = new URLSearchParams(filters);
    const response = await this.api.get(`/transactions?${params}`);
    return response.data;
  }

  async getTransaction(id) {
    const response = await this.api.get(`/transactions/${id}`);
    return response.data;
}

async bulkImportTransactions(file) {
    const form = new FormData();
    form.append('csv_file', file);
    const response = await this.api.post('/transactions/bulk-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

  async createTransaction(data) {
    const response = await this.api.post('/transactions', data);
    return response.data.data;
  }

  async updateTransaction(id, data) {
    const response = await this.api.put(`/transactions/${id}`, data);
    return response.data.data;
  }

  async deleteTransaction(id) {
    await this.api.delete(`/transactions/${id}`);
  }

  // Recurring Templates
  async getRecurringTemplates() {
    const response = await this.api.get('/recurring-templates');
    return response.data.data;
  }

  async createRecurringTemplate(data) {
    const response = await this.api.post('/recurring-templates', data);
    return response.data.data;
  }

  async updateRecurringTemplate(id, data) {
    const response = await this.api.put(`/recurring-templates/${id}`, data);
    return response.data.data;
  }

  async deleteRecurringTemplate(id) {
    await this.api.delete(`/recurring-templates/${id}`);
  }

  async processRecurringTemplate(id) {
    const response = await this.api.post(`/recurring-templates/${id}/process`);
    return response.data;
  }

  // Reports
  async generateReport(config) {
    const response = await this.api.post('/reports/generate', config);
    return response.data;
  }

  async downloadReport(config) {
    const params = new URLSearchParams({
      type: config.type,
      start_date: config.start_date,
      end_date: config.end_date,
    });
    const response = await this.api.get(`/reports/download?${params}`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

export const api = new ApiService();

// Extract a user-friendly message from an Axios error
export function getErrorMessage(error, fallback = 'Something went wrong') {
  if (!error.response) return 'Network error. Please check your connection.';
  const { data, status } = error.response;
  if (status === 422 && data.errors) {
    // Return first validation error
    const firstField = Object.keys(data.errors)[0];
    return data.errors[firstField][0];
  }
  return data?.message || data?.error || fallback;
}